import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, AuthState, User } from '../types/auth';
import { authService } from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true,
  });

  const checkAuth = async () => {
    let token = authService.getToken();
    
    // If no local token, check for cross-subdomain authentication
    if (!token) {
      const foundCrossSubdomainAuth = authService.checkCrossSubdomainAuth();
      if (foundCrossSubdomainAuth) {
        token = authService.getToken(); // Get the token that was set from cookie
        console.log("Using cross-subdomain authentication");
      }
    }
    
    if (!token) {
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
      return;
    }

    try {
      // We have a token - get basic user info
      // Note: We don't verify the token here to avoid CORS issues
      // The backend Lambda authorizer will verify it on every API call
      const user = await authService.getUserInfo();
      
      setAuthState({
        isAuthenticated: true,
        user,
        token,
        loading: false,
      });
      
      console.log('[AuthContext] Authentication established with token');
    } catch (error) {
      console.error('[AuthContext] Auth check failed:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
    }
  };

  const login = () => {
    authService.redirectToLogin();
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('bri_refresh_token');
    await authService.logout(refreshToken || undefined);
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
    });
    // Redirect to home page after logout
    window.location.href = '/';
  };

  const refreshToken = async (): Promise<string | null> => {
    const newToken = await authService.refreshToken();
    if (newToken) {
      const user = await authService.getUserInfo();
      setAuthState(prev => ({
        ...prev,
        token: newToken,
        user,
        isAuthenticated: true,
      }));
    }
    return newToken;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (authState.isAuthenticated && authState.token) {
      const interval = setInterval(async () => {
        await refreshToken();
      }, 15 * 60 * 1000); // Refresh every 15 minutes

      return () => clearInterval(interval);
    }
  }, [authState.isAuthenticated, authState.token]);

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshToken,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

